import { ISDUIResponsiveValue } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import { SDUILayoutRendererItem } from "@faire/retailer-visitor-shared/components/SDUI/LayoutRenderers/types";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { IComponent } from "@faire/web-api--source/indigofair/data/IComponent";
import { IFlowLayout } from "@faire/web-api--source/indigofair/data/IFlowLayout";
import { ILayout } from "@faire/web-api--source/indigofair/data/ILayout";
import { ILayoutSection } from "@faire/web-api--source/indigofair/data/ILayoutSection";
import { IResponsiveComponentLayoutConfig } from "@faire/web-api--source/indigofair/data/IResponsiveComponentLayoutConfig";
import { IResponsiveComponentLayoutConfigs } from "@faire/web-api--source/indigofair/data/IResponsiveComponentLayoutConfigs";
import { WebClientSize } from "@faire/web-api--source/indigofair/data/WebClientSize";

/**
 * This has to be the lowest common multiple (LCM) of possible component widths
 * in order for every possible width to fit into a whole number of columns.
 * E.g. if we have a
 *  - 5 column layout in the first row
 *  - 12 column layout in second row
 *  - 6 column layout in third row
 * We need to have a grid that can be divided into 5, 12, and 6 columns. 60 is
 * the lowest number of columns that can support that division.
 *
 * Currently, we have a 12 column layout and we support components
 * spanning 1, 2, 3, 4, 5, 6, and 12 columns; the LCM of those is 60.
 */
export const NUM_COLUMNS_GRID_DESKTOP = 60;
/**
 * Mobile does not need support for 5 column layouts.
 * The gaps with 60 columns take up space and overflow the smallest
 * supported screen sizes (e.g. 60 columns x 8px = 480px).
 */
export const NUM_COLUMNS_GRID = 12;

const INITIAL_ACC_VALUE: {
  responsiveItemWidths: ISDUIResponsiveValue<(number | undefined)[]>;
  currentWidthIndices: WidthIndices;
} = {
  responsiveItemWidths: {
    mobile_and_above: [],
    tablet_and_above: [],
    desktop_and_above: [],
    x_large_and_above: [],
  },
  currentWidthIndices: {
    mobile: 0,
    tablet: 0,
    desktop: 0,
    xl_desktop: 0,
  },
};

type WidthIndices = {
  mobile: number;
  tablet: number;
  desktop: number;
  xl_desktop: number;
};

/**
 * Converts a percentage width of a component to the number of columns
 * in the grid layout that the component should span.
 */
export const calcNumColumnsToSpan = (
  percentageWidth: number | undefined,
  isMobile = false,
  numColumns?: number | undefined | undefined
) =>
  Math.round(
    (numColumns ?? (isMobile ? NUM_COLUMNS_GRID : NUM_COLUMNS_GRID_DESKTOP)) *
      ((percentageWidth ?? 100) / 100)
  );

/**
 * Calculates the column span for the item in each screen size based on item visibility.
 * itemWidths only includes as many widths as number of visible items, so we need to account for that.
 * @param itemWidths - The widths of the item in each screen size
 * @param index - The index of the item in the itemWidth array
 * @param numColumnsPerRow - The number of columns per row for the flow layout
 * @returns - The column span for the item in each screen size, as well as the updated indices
 * for where in the itemWidth array we are. Returns undefined for the screen size if the item is not visible.
 */
export const getColSpanFromItemWidths = (
  itemWidths: ISDUIResponsiveValue<(number | undefined)[]>,
  index: number,
  numColumnsPerRow?: ISDUIResponsiveValue<number>
): { colSpan: ISDUIResponsiveValue<number> } => {
  const mobileColSpan = itemWidths.mobile_and_above?.[index]
    ? calcNumColumnsToSpan(
        itemWidths.mobile_and_above[index],
        true,
        numColumnsPerRow?.mobile_and_above
      )
    : undefined;

  const tabletColSpan = itemWidths.tablet_and_above?.[index]
    ? calcNumColumnsToSpan(
        itemWidths.tablet_and_above[index],
        true,
        numColumnsPerRow?.tablet_and_above
      )
    : undefined;

  const desktopColSpan = itemWidths.desktop_and_above?.[index]
    ? calcNumColumnsToSpan(
        itemWidths.desktop_and_above[index],
        false,
        numColumnsPerRow?.desktop_and_above
      )
    : undefined;

  let xlargeColSpan = desktopColSpan;
  if (itemWidths.x_large_and_above) {
    xlargeColSpan = itemWidths.x_large_and_above?.[index]
      ? calcNumColumnsToSpan(
          itemWidths.x_large_and_above[index],
          false,
          numColumnsPerRow?.x_large_and_above
        )
      : undefined;
  }

  return {
    colSpan: {
      mobile_and_above: mobileColSpan,
      tablet_and_above: tabletColSpan,
      desktop_and_above: desktopColSpan,
      x_large_and_above: xlargeColSpan,
    },
  };
};

export const getItemWidthsFromViewportVisibility = (
  itemWidths: ISDUIResponsiveValue<number[]>,
  items: SDUILayoutRendererItem[]
) => {
  const { responsiveItemWidths, currentWidthIndices: _ } = items.reduce(
    (acc, item) => {
      const shouldDisplayOnMobile = isItemVisible(
        item.itemVisibility,
        WebClientSize.MOBILE
      );
      const shouldDisplayOnTablet = isItemVisible(
        item.itemVisibility,
        WebClientSize.TABLET
      );
      const shouldDisplayOnDesktop = isItemVisible(
        item.itemVisibility,
        WebClientSize.DESKTOP
      );
      const shouldDisplayOnXLargeAndAbove = isItemVisible(
        item.itemVisibility,
        WebClientSize.XLARGE_DESKTOP
      );

      const newResponsiveItemWidths: ISDUIResponsiveValue<
        (number | undefined)[]
      > = {
        mobile_and_above: [
          ...(acc.responsiveItemWidths.mobile_and_above ?? []),
          shouldDisplayOnMobile
            ? itemWidths.mobile_and_above?.[acc.currentWidthIndices.mobile]
            : undefined,
        ],
        tablet_and_above: [
          ...(acc.responsiveItemWidths.tablet_and_above ?? []),
          shouldDisplayOnTablet
            ? itemWidths.tablet_and_above?.[acc.currentWidthIndices.tablet]
            : undefined,
        ],
        desktop_and_above: [
          ...(acc.responsiveItemWidths.desktop_and_above ?? []),
          shouldDisplayOnDesktop
            ? itemWidths.desktop_and_above?.[acc.currentWidthIndices.desktop]
            : undefined,
        ],
        x_large_and_above: [
          ...(acc.responsiveItemWidths.x_large_and_above ?? []),
          shouldDisplayOnXLargeAndAbove
            ? (itemWidths.x_large_and_above?.[
                acc.currentWidthIndices.xl_desktop
              ] ??
              itemWidths.desktop_and_above?.[acc.currentWidthIndices.desktop])
            : undefined,
        ],
      };

      // itemWidths only includes as many widths as number of visible items.
      // therefore, we need to increment the index only if the item is visible
      // for that particular screen size
      const newWidthIndices: WidthIndices = {
        mobile:
          acc.currentWidthIndices.mobile + (shouldDisplayOnMobile ? 1 : 0),
        tablet:
          acc.currentWidthIndices.tablet + (shouldDisplayOnTablet ? 1 : 0),
        desktop:
          acc.currentWidthIndices.desktop + (shouldDisplayOnDesktop ? 1 : 0),
        xl_desktop:
          acc.currentWidthIndices.xl_desktop +
          (shouldDisplayOnXLargeAndAbove ? 1 : 0),
      };

      return {
        responsiveItemWidths: newResponsiveItemWidths,
        currentWidthIndices: newWidthIndices,
      };
    },
    INITIAL_ACC_VALUE
  );

  return responsiveItemWidths;
};

const isItemVisible = (
  itemVisibility: Array<keyof typeof WebClientSize> | undefined,
  clientSize: keyof typeof WebClientSize
) => {
  if (!itemVisibility || itemVisibility.length === 0) {
    return true; // default to visible at all screen sizes
  }
  return itemVisibility.includes(clientSize);
};

// Returns an array of widths for the items in the layout config
// If the item is not in the layout config, the width is undefined
export const getItemWidths = (
  layoutConfig: IResponsiveComponentLayoutConfig[],
  numItems: number
): (number | undefined)[] => {
  const componentIndexToWidthMap = new Map<number, number | undefined>();
  layoutConfig.forEach((config) => {
    componentIndexToWidthMap.set(
      config.component_index ?? -1,
      config.component_width
    );
  });
  const widths = Array(numItems).fill(0);
  for (let i = 0; i < numItems; i++) {
    const width = componentIndexToWidthMap.get(i);
    if (width === undefined || width === 0) {
      widths[i] = undefined;
    } else {
      widths[i] = width;
    }
  }
  return widths;
};

// Returns an array of orders for the items in the layout config
// If the item is not in the layout config, the order is last
export const getItemOrders = (
  layoutConfig: IResponsiveComponentLayoutConfig[],
  numItems: number
): number[] => {
  const componentIndexToOrderMap = new Map<number, number>();
  layoutConfig.forEach((config, i) => {
    componentIndexToOrderMap.set(config.component_index ?? -1, i);
  });
  const componentOrders = Array(numItems).fill(0);
  for (let i = 0; i < numItems; i++) {
    const componentOrder = componentIndexToOrderMap.get(i);
    // if the component is not in the layout config, order it last
    componentOrders[i] = componentOrder ?? numItems;
  }
  return componentOrders;
};

export const getLCMOfNumColumnsGivenWidths = (
  widths: (number | undefined)[]
) => {
  if (widths.length === 0) {
    return NUM_COLUMNS_GRID_DESKTOP;
  }
  const numColumnsPerRow = widths.map((width) => {
    if (width === undefined || width === 0) {
      return 1;
    }
    return Math.floor(100 / width);
  });
  const gcd = (a: number, b: number): number => {
    return !b ? a : gcd(b, a % b);
  };
  return numColumnsPerRow.reduce((a, b) => (a * b) / gcd(a, b));
};

/**
 * Computes a new layout section as if the existing flow layout is decreased by
 * one column per row. It only applies the change for desktop and larger viewports.
 * @param section the origincal layout section with a flow layout
 * @param truncateToOriginalRowCount whether to truncate the new layout config to have the same number of tile rows as the original
 * @param keepLastTile whether to keep the last tile when truncating to original row count
 * @returns a new layout section with the flow layout decreased by one column per row
 */
export const getVisibleSidebarLayoutSection = (
  originalLayoutSection: ILayoutSection,
  truncateToOriginalRowCount = false,
  keepLastTile = false
): ILayoutSection | undefined => {
  if (!originalLayoutSection.layout?.flow_layout) {
    return undefined;
  }

  const numComponents = originalLayoutSection.components.length;

  const layoutConfigs =
    originalLayoutSection.layout.flow_layout
      .responsive_component_layout_configs;

  const desktopLayoutConfigOriginal = layoutConfigs?.desktop_and_above ?? [];
  const xLargeLayoutConfigOriginal =
    layoutConfigs?.x_large_and_above ?? desktopLayoutConfigOriginal;

  if (
    numComponents === 0 ||
    (!desktopLayoutConfigOriginal && !xLargeLayoutConfigOriginal)
  ) {
    return undefined;
  }

  const desktopLayoutConfigNew: IResponsiveComponentLayoutConfig[] =
    decreaseLayoutConfigByOneColumn(
      desktopLayoutConfigOriginal,
      originalLayoutSection.components ?? [],
      truncateToOriginalRowCount,
      keepLastTile
    );
  const xLargeLayoutConfigNew: IResponsiveComponentLayoutConfig[] =
    decreaseLayoutConfigByOneColumn(
      xLargeLayoutConfigOriginal,
      originalLayoutSection.components ?? [],
      truncateToOriginalRowCount,
      keepLastTile
    );

  return ILayoutSection.build({
    ...originalLayoutSection,
    layout: ILayout.build({
      flow_layout: IFlowLayout.build({
        ...originalLayoutSection.layout.flow_layout,
        responsive_component_layout_configs:
          IResponsiveComponentLayoutConfigs.build({
            ...layoutConfigs,
            desktop_and_above:
              desktopLayoutConfigOriginal.length > 0
                ? desktopLayoutConfigNew
                : undefined,
            x_large_and_above:
              xLargeLayoutConfigOriginal.length > 0
                ? xLargeLayoutConfigNew
                : undefined,
          }),
      }),
    }),
  });
};

/**
 * Decreases the layout config by one column per row when possible. It calculates the original and new
 * number of tiles per row based on the original layout config of tile wrapper components in the components array.
 * @param layoutConfig the original layout config array
 * @param components the components in the layout section
 * @param truncateToOriginalRowCount whether to truncate the new layout config to have the same number of tile rows as the original
 * @param keepLastTile whether to keep the last tile when truncating to original row count
 * @returns a new layout config with the flow layout decreased by one column per row. If
 * there are no tile wrapper components, or if the tile wrapper component width is not defined,
 * or if the original layout has only one tile per row, then the original layout config is
 * returned without modification.
 */
export const decreaseLayoutConfigByOneColumn = (
  layoutConfig: IResponsiveComponentLayoutConfig[],
  components: IComponent[],
  truncateToOriginalRowCount = false,
  keepLastTile = false
): IResponsiveComponentLayoutConfig[] => {
  // find a tile wrapper component to use to check how many products there are per row
  const someTileWrapperComponentIndex = components.findIndex(
    (component) => component.tile_wrapper_component
  );
  // if there are no tiles we don't have anything to reconfigure
  if (someTileWrapperComponentIndex === -1) {
    return layoutConfig;
  }

  // get the component_width of the tile wrapper component found above
  const tileWrapperComponentWidth = layoutConfig.find(
    (config) => config.component_index === someTileWrapperComponentIndex
  )?.component_width;
  if (tileWrapperComponentWidth === undefined) {
    return layoutConfig;
  }

  // calculate how many products per row based on the tile wrapper component width
  const numTilesPerRow = Math.floor(100 / tileWrapperComponentWidth);

  // don't proceed if there is originally only 1 tile per row
  if (numTilesPerRow <= 1) {
    return layoutConfig;
  }

  const rowIndexMapOfFullWidthComponents = getRowIndexOfFullWidthComponents(
    layoutConfig,
    numTilesPerRow
  );

  const numFullWidthComponents = rowIndexMapOfFullWidthComponents.size;
  const numTileWrapperComponents = components.length - numFullWidthComponents;

  // calculate the new width of the tile wrapper component, leaving a full row for the full-width components
  const newTileWrapperComponentWidth = Math.floor(100 / (numTilesPerRow - 1));

  // Calculate the original number of rows of tile components (excluding full-width rows)
  const originalNumTileRows = Math.ceil(
    (layoutConfig.length - numFullWidthComponents) / numTilesPerRow
  );

  const newLayoutConfig: IResponsiveComponentLayoutConfig[] = Array(
    numTileWrapperComponents + numFullWidthComponents * (numTilesPerRow - 1)
  ).fill(undefined);

  // assign the full-width components to the new layout config before the tile wrapper components
  rowIndexMapOfFullWidthComponents.forEach((config, index) => {
    newLayoutConfig[index * (numTilesPerRow - 1)] = config;
  });

  for (
    let iComponent = 0, iConfigArray = 0;
    iComponent < components.length && iConfigArray < newLayoutConfig.length;

  ) {
    if (newLayoutConfig[iConfigArray] !== undefined) {
      // this row is already filled with a full-width component, so proceed to the next row's config array index
      iConfigArray += numTilesPerRow - 1;
      continue;
    }

    if (layoutConfig[iComponent]?.component_width === 100) {
      // full-width components have already been assigned to their spots, so proceed to the next component
      iComponent++;
      continue;
    }

    if (layoutConfig[iComponent] === undefined) {
      iComponent++;
      continue;
    }

    newLayoutConfig[iConfigArray] = IResponsiveComponentLayoutConfig.build({
      ...layoutConfig[iComponent]!,
      component_width: newTileWrapperComponentWidth,
    });
    iConfigArray++;
    iComponent++;
  }

  // remove undefined elements that were holding space for full-width components
  let result = newLayoutConfig.filter(isNotUndefined);

  // If truncation is requested, limit tile rows to the same number as the original
  // This is needed to support Brand Search on Search Page.
  if (truncateToOriginalRowCount) {
    // Get all full-width components
    const fullWidthComponentConfigs = result.filter(
      (config) => config.component_width === 100
    );

    // Get all tile components
    const tileComponentConfigs = result.filter(
      (config) => config.component_width !== 100
    );

    // Calculate the new number of tile rows
    const newNumTileRows = Math.ceil(
      tileComponentConfigs.length / (numTilesPerRow - 1)
    );

    if (newNumTileRows > originalNumTileRows) {
      // Calculate how many tile components to keep to maintain the original row count
      const numTilesToKeep = originalNumTileRows * (numTilesPerRow - 1);

      const truncatedTileComponentConfigs = tileComponentConfigs.slice(
        0,
        numTilesToKeep
      );

      result = result.filter((config) => {
        if (config.component_width === 100) {
          return fullWidthComponentConfigs.includes(config);
        }
        return truncatedTileComponentConfigs.includes(config);
      });

      // If we need to keep the last tile and we're truncating
      if (keepLastTile && numTilesToKeep < tileComponentConfigs.length) {
        const lastRealTile =
          tileComponentConfigs[tileComponentConfigs.length - 1];
        if (lastRealTile) {
          const lastTruncatedTileIndex = result.findLastIndex(
            (config) => config.component_width !== 100
          );
          if (lastTruncatedTileIndex >= 0) {
            // Replace the last tile in the truncated list with the actual last tile
            result[lastTruncatedTileIndex] = lastRealTile;
          }
        }
      }
    }
  }

  return result;
};

/**
 * Returns a map of the row index and the layout config for the full-width components in the layout config.
 * To be used when decreasing the layout config by one column per row.
 * @param layoutConfig the layout config array
 * @param numComponentsPerRow the number of components per row
 * @returns a map of the row index and the layout config for the full-width components in the layout config
 */
const getRowIndexOfFullWidthComponents = (
  layoutConfig: IResponsiveComponentLayoutConfig[],
  numComponentsPerRow: number
): Map<number, IResponsiveComponentLayoutConfig> => {
  let currentRowIndex = 0;
  let currentTileIndex = 1;
  const resultsMap: Map<number, IResponsiveComponentLayoutConfig> = new Map();
  for (let i = 0; i < layoutConfig.length; i++) {
    if (layoutConfig[i] === undefined) {
      continue;
    }

    if (
      layoutConfig[i]!.component_width !== 100 &&
      currentTileIndex % numComponentsPerRow !== 0
    ) {
      currentTileIndex++;
    } else if (layoutConfig[i]!.component_width !== 100) {
      currentTileIndex = 1;
      currentRowIndex++;
    } else {
      resultsMap.set(currentRowIndex, layoutConfig[i]!);
      currentRowIndex++;
      currentTileIndex = 1;
    }
  }
  return resultsMap;
};
